import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { ActionItem, ActionsMenu, Table } from 'common/components';
import { ReactComponent as DeleteIcon } from 'img/icons/trash.svg';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import { deleteTimeclockById } from 'modules/timeclock/state/actions';
import { IFilterSettings } from 'common/interfaces/filter';
import { IBodyCell, IHeadCell, ITableParams, ITableRow } from 'common/interfaces/table';
import tableHeaders from 'common/messages/tableHeaders';
import tableFilters from 'common/messages/tableFilters';
import inputLabels from 'common/messages/inputLabels';
import commonMessages from 'common/messages/messages';
import { payTypeLabels } from 'modules/employees/constants';
import { ITimeclockListItem } from 'modules/timeclock/interfaces/timeclock';
import { IPageMeta } from 'common/interfaces/pagination';
import { calculateDuration } from 'common/utils/calculate';
import { DurationUnit } from 'common/interfaces/common';
import { DEFAULT_DATE_TIME_FORMAT } from 'common/constants/dateFormats';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import EmployeeLinkWithAvatar from 'common/components/ProfileLinkWithAvatar/EmployeeLinkWithAvatar';
import { HEADER_ACTIONS_OPTION } from 'constants/table.constants';

const useStyles = makeStyles(() => ({
  timeclockTable: {
    flex: 1,
    minHeight: 0,
    height: 'unset',
  },
}));

interface ITimeclockLogTable {
  tableFilterSettings: IFilterSettings[];
  onChangeTableParams?: (tableParams: ITableParams) => void;
  isLoading: boolean;
  data: ITimeclockListItem[];
  meta?: IPageMeta;
  tableParams?: ITableParams;
  onEventClick: (event) => void;
}

const TimeclockLogTable: FC<ITimeclockLogTable> = ({
  tableFilterSettings,
  onChangeTableParams,
  isLoading,
  data,
  meta,
  tableParams,
  onEventClick,
}) => {
  const [timezoneMoment] = useTimezoneMoment();
  const dispatch = useDispatch();
  const intl = useIntl();
  const classes = useStyles();
  const { TIME_CLOCK_DELETE } = usePermissionContext();
  const timeClockLogs = data || [];

  const headerOptions: IHeadCell[] = [
    {
      id: 'employee',
      label: intl.formatMessage(tableHeaders.employee),
      sort: true,
    },
    {
      id: 'club',
      label: intl.formatMessage(tableHeaders.club),
      sort: true,
    },
    {
      id: 'department',
      label: intl.formatMessage(tableFilters.department),
      sort: true,
    },
    {
      id: 'clock_in',
      label: intl.formatMessage(tableHeaders.clockIn),
      sort: true,
    },
    {
      id: 'clock_out',
      label: intl.formatMessage(tableHeaders.clockOut),
      sort: true,
    },
    {
      id: 'duration',
      label: intl.formatMessage(inputLabels.duration),
      sort: false,
    },
    {
      id: 'pay_type',
      label: intl.formatMessage(inputLabels.payType),
      sort: true,
    },
    {
      id: 'unit',
      label: intl.formatMessage(tableHeaders.unit),
      sort: true,
    },
  ];

  if (TIME_CLOCK_DELETE) {
    headerOptions.push(HEADER_ACTIONS_OPTION);
  }

  const tableRows: ITableRow[] = timeClockLogs.map(item => {
    const bodyCells: IBodyCell[] = [
      {
        label: '',
        className: 'body-avatar-cell',
        cellComponent: <EmployeeLinkWithAvatar employee={item.employee} />,
      },
      {
        label: '',
        className: 'body-cell',
        cellComponent: <Typography variant="body1">{item.clubTitle}</Typography>,
      },
      {
        label: '',
        className: 'body-cell',
        cellComponent: <Typography variant="body1">{item.departmentTitle}</Typography>,
      },
      {
        label: '',
        className: 'body-cell',
        cellComponent: (
          <Typography variant="body1">
            {timezoneMoment(item.clockIn).format(DEFAULT_DATE_TIME_FORMAT)}
          </Typography>
        ),
      },
      {
        label: '',
        className: 'body-cell',
        cellComponent: (
          <Typography variant="body1">
            {item.clockOut ? timezoneMoment(item.clockOut).format(DEFAULT_DATE_TIME_FORMAT) : ''}
          </Typography>
        ),
      },
      {
        label: '',
        className: 'body-cell',
        cellComponent: (
          <Typography variant="body1">
            {item.durationInMinutes
              ? calculateDuration(DurationUnit.Mins, item.durationInMinutes)
              : ''}
          </Typography>
        ),
      },
      {
        label: '',
        className: 'body-cell',
        cellComponent: (
          <Typography variant="body1">{payTypeLabels.translate(item.payType)}</Typography>
        ),
      },
      {
        label: '',
        className: 'body-cell',
        cellComponent: item?.eventPerson?.eventPersonId ? (
          <Box onClick={() => onEventClick(item?.eventPerson)}>
            <Typography variant="body1">{item?.eventPerson?.title}</Typography>
          </Box>
        ) : (
          ''
        ),
      },
    ];

    if (TIME_CLOCK_DELETE) {
      bodyCells.push({
        label: 'actions',
        className: 'body-actions-cell',
        cellComponent: (
          <>
            <ActionsMenu horizontal tableActionsMode>
              <ActionItem
                key="1"
                icon={<DeleteIcon width={16} height={16} />}
                onClick={() => dispatch(deleteTimeclockById(item.id))}
              >
                <FormattedMessage {...commonMessages.deleteBtn} />
              </ActionItem>
            </ActionsMenu>
          </>
        ),
      });
    }

    return {
      id: item.id,
      cells: bodyCells,
    };
  });

  return (
    <Table
      isLoading={isLoading}
      headerOptions={headerOptions}
      filters={tableFilterSettings}
      rows={tableRows}
      onChangeParams={onChangeTableParams}
      hideSearchInput
      showPerPageSelect
      page={meta?.page}
      tableParams={tableParams}
      className={classes.timeclockTable}
    />
  );
};

export default TimeclockLogTable;
